// Single source of truth for block types, defaults, and which props the
// inspector exposes. Adding a new block type = add an entry here, render it
// on the client (BlockRenderer.jsx), and render it on the server
// (pubvora-web/app/Services/BlockRenderer.php).

export const BLOCK_DEFS = {
  // ── layout / containers ──────────────────────────────────────────────
  section: {
    label: 'Section',
    icon: '▭',
    accepts: ['heading', 'text', 'image', 'button', 'columns', 'spacer', 'video', 'divider', 'quote', 'accordion'],
    defaults: () => ({
      props: { style: { background: '#ffffff', color: '#0b0b0e', padding: 'lg' } },
      children: [],
    }),
    fields: [
      { key: 'style.background', label: 'Background', type: 'color' },
      { key: 'style.color', label: 'Text color', type: 'color' },
      {
        key: 'style.padding',
        label: 'Padding',
        type: 'select',
        options: [
          { value: 'sm', label: 'Small' },
          { value: 'md', label: 'Medium' },
          { value: 'lg', label: 'Large' },
          { value: 'xl', label: 'Extra large' },
        ],
      },
    ],
  },
  columns: {
    label: 'Columns',
    icon: '☰',
    // Columns can only contain `column` children. Add/remove columns from
    // the inspector. Each column is its own droppable bucket.
    accepts: ['column'],
    defaults: () => ({
      props: { gap: 24 },
      children: [newBlock('column'), newBlock('column')],
    }),
    fields: [
      { key: 'gap', label: 'Gap (px)', type: 'number' },
      { key: '__columnControls__', label: 'Columns', type: 'columnControls' },
    ],
  },
  column: {
    label: 'Column',
    icon: '│',
    // A single bucket inside a Columns block. Accepts the same content set
    // as a Section (minus other layout containers, to keep nesting sane).
    accepts: ['heading', 'text', 'image', 'button', 'spacer', 'video', 'divider', 'quote', 'accordion'],
    paletteHidden: true,
    defaults: () => ({ props: {}, children: [] }),
    fields: [],
  },

  // ── chrome ──────────────────────────────────────────────────────────
  navbar: {
    label: 'Navbar',
    icon: '☰',
    defaults: () => ({
      props: {
        brand: 'Pubvora',
        links: [
          { label: 'Features', href: '#features' },
          { label: 'Pricing', href: '#pricing' },
          { label: 'About', href: '#about' },
        ],
        cta: { label: 'Get started', href: '#' },
      },
    }),
    fields: [
      { key: 'brand', label: 'Brand', type: 'text' },
      { key: 'links', label: 'Links (label|href, one per line)', type: 'pairs' },
      { key: 'cta.label', label: 'CTA label', type: 'text' },
      { key: 'cta.href', label: 'CTA link', type: 'text' },
    ],
  },
  footer: {
    label: 'Footer',
    icon: '▁',
    defaults: () => ({
      props: {
        brand: 'Pubvora',
        tagline: 'Build, launch, grow.',
        copyright: `© ${new Date().getFullYear()} Pubvora. All rights reserved.`,
        groups: [
          {
            heading: 'Product',
            links: [
              { label: 'Features', href: '#' },
              { label: 'Pricing', href: '#' },
            ],
          },
          {
            heading: 'Company',
            links: [
              { label: 'About', href: '#' },
              { label: 'Contact', href: '#' },
            ],
          },
        ],
      },
    }),
    fields: [
      { key: 'brand', label: 'Brand', type: 'text' },
      { key: 'tagline', label: 'Tagline', type: 'text' },
      { key: 'copyright', label: 'Copyright line', type: 'text' },
      { key: 'groups', label: 'Link groups', type: 'footerGroups' },
    ],
  },

  // ── typography ──────────────────────────────────────────────────────
  heading: {
    label: 'Heading',
    icon: 'H',
    inlineEditable: 'text',
    defaults: () => ({ props: { text: 'Section heading', level: 2 } }),
    fields: [
      { key: 'text', label: 'Text', type: 'textarea' },
      {
        key: 'level',
        label: 'Level',
        type: 'select',
        options: [1, 2, 3, 4, 5, 6].map((n) => ({ value: n, label: `H${n}` })),
      },
    ],
  },
  text: {
    label: 'Text',
    icon: '¶',
    inlineEditable: 'text',
    defaults: () => ({
      props: { text: 'Write something compelling here.' },
    }),
    fields: [{ key: 'text', label: 'Text', type: 'textarea' }],
  },
  quote: {
    label: 'Quote',
    icon: '❝',
    defaults: () => ({
      props: {
        text: 'Pubvora replaced four tools we were paying for. Shipped a launch in three days.',
        author: 'A founder',
      },
    }),
    fields: [
      { key: 'text', label: 'Quote', type: 'textarea' },
      { key: 'author', label: 'Author', type: 'text' },
    ],
  },

  // ── media ───────────────────────────────────────────────────────────
  image: {
    label: 'Image',
    icon: '◰',
    defaults: () => ({ props: { src: '', alt: '' } }),
    fields: [
      { key: 'src', label: 'Image', type: 'imageUrl' },
      { key: 'alt', label: 'Alt text', type: 'text' },
    ],
  },
  video: {
    label: 'Video',
    icon: '▶',
    defaults: () => ({ props: { url: '', caption: '' } }),
    fields: [
      { key: 'url', label: 'Video URL (YouTube or Vimeo)', type: 'text' },
      { key: 'caption', label: 'Caption', type: 'text' },
    ],
  },

  // ── interactive ─────────────────────────────────────────────────────
  button: {
    label: 'Button',
    icon: '⏵',
    defaults: () => ({
      props: { label: 'Click me', href: '#', variant: 'primary' },
    }),
    fields: [
      { key: 'label', label: 'Label', type: 'text' },
      { key: 'href', label: 'Link', type: 'text' },
      {
        key: 'variant',
        label: 'Style',
        type: 'select',
        options: [
          { value: 'primary', label: 'Primary' },
          { value: 'secondary', label: 'Secondary' },
          { value: 'ghost', label: 'Ghost' },
        ],
      },
    ],
  },
  accordion: {
    label: 'Accordion',
    icon: '⌄',
    defaults: () => ({
      props: {
        items: [
          { question: 'How does Pubvora compare to Webflow?', answer: 'It bundles commerce, courses, and CRM in one workspace.' },
          { question: 'Can I bring my own AI key?', answer: 'Yes — set it per workspace in Settings.' },
        ],
      },
    }),
    fields: [{ key: 'items', label: 'Items (Q & A)', type: 'qaList' }],
  },

  // ── utility ─────────────────────────────────────────────────────────
  divider: {
    label: 'Divider',
    icon: '─',
    defaults: () => ({ props: { color: '#e5e7eb', thickness: 1 } }),
    fields: [
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'thickness', label: 'Thickness (px)', type: 'number' },
    ],
  },
  spacer: {
    label: 'Spacer',
    icon: '↕',
    defaults: () => ({ props: { height: 32 } }),
    fields: [{ key: 'height', label: 'Height (px)', type: 'number' }],
  },
};

export const PALETTE_GROUPS = [
  { label: 'Layout', types: ['section', 'columns', 'spacer', 'divider'] },
  { label: 'Chrome', types: ['navbar', 'footer'] },
  { label: 'Content', types: ['heading', 'text', 'quote', 'button'] },
  { label: 'Media', types: ['image', 'video'] },
  { label: 'Interactive', types: ['accordion'] },
];

export function blockHasChildren(type) {
  return Array.isArray(BLOCK_DEFS[type]?.accepts);
}

export function blockAccepts(parentType, childType) {
  const def = BLOCK_DEFS[parentType];
  if (!def?.accepts) return false;
  return def.accepts.includes(childType);
}

export function isInlineEditable(type) {
  return BLOCK_DEFS[type]?.inlineEditable === 'text';
}

export function newBlock(type) {
  const def = BLOCK_DEFS[type];
  if (!def) throw new Error(`Unknown block type: ${type}`);
  const base = def.defaults();
  return {
    id: cryptoId(),
    type,
    props: structuredClone(base.props || {}),
    ...(base.children !== undefined ? { children: base.children } : {}),
  };
}

function cryptoId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'b_' + Math.random().toString(36).slice(2, 10);
}

// path-based getters/setters for nested prop keys ("style.background")
export function getProp(block, key) {
  return key.split('.').reduce((o, k) => (o ? o[k] : undefined), block.props);
}

export function setProp(block, key, value) {
  const next = { ...block, props: structuredClone(block.props || {}) };
  const parts = key.split('.');
  let cur = next.props;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = cur[parts[i]] ? { ...cur[parts[i]] } : {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
}
